import React, { useCallback } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Layout } from "antd";
import { Link } from "react-router-dom";
import clone from "clone";
import { connect } from "react-redux";
import IntlMessages from "../../../components/utility/intlMessages";
import Logo from "../../../components/utility/logo";
import Menu from "../../../components/uielements/menu";
import Scrollbars from "../../../components/utility/customScrollBar";
import SidebarWrapper from "./sidebar.style";
import appActions from "../../../redux/app/actions";
import options from "./options";
import { usePermissions } from "../../../query/api";
import {
  satisfyPermissionSets,
  useIsSuperPlusAdmin,
} from "../../../hooks/permission";
import { themeConfig } from "../../../settings";
import themes from "../../../settings/themes";

const { SubMenu } = Menu;
const { Sider } = Layout;

const { toggleOpenDrawer, changeOpenKeys, changeCurrent, toggleCollapsed } =
  appActions;
const stripTrailingSlash = (str) => {
  if (str.substr(-1) === "/") {
    return str.substr(0, str.length - 1);
  }
  return str;
};

const Sidebar = ({
  app,
  toggleOpenDrawer,
  changeOpenKeys,
  changeCurrent,
  toggleCollapsed,
  height,
  url: sidebarUrl,
}) => {
  const { data } = usePermissions();
  const isSuperPlusAdmin = useIsSuperPlusAdmin();

  const handleClick = useCallback(
    (e) => {
      changeCurrent([e.key]);
      if (app.view === "MobileView") {
        setTimeout(() => {
          toggleCollapsed();
          toggleOpenDrawer();
        }, 100);
      }
    },
    [app.view, changeCurrent, toggleCollapsed, toggleOpenDrawer],
  );

  const getAncestorKeys = useCallback((key) => {
    const map = {
      sub3: ["sub2"],
    };
    return map[key] || [];
  }, []);

  const onOpenChange = useCallback(
    (newOpenKeys) => {
      const latestOpenKey = newOpenKeys.find(
        (key) => !(app.openKeys.indexOf(key) > -1),
      );
      const latestCloseKey = app.openKeys.find(
        (key) => !(newOpenKeys.indexOf(key) > -1),
      );
      let nextOpenKeys = [];
      if (latestOpenKey) {
        nextOpenKeys = getAncestorKeys(latestOpenKey).concat(latestOpenKey);
      }
      if (latestCloseKey) {
        nextOpenKeys = getAncestorKeys(latestCloseKey);
      }
      changeOpenKeys(nextOpenKeys);
    },
    [app.openKeys, changeOpenKeys, getAncestorKeys],
  );

  const getMenuItem = useCallback(
    ({ singleOption, submenuStyle, submenuColor }) => {
      const { key, label, leftIcon, children } = singleOption;
      const url = stripTrailingSlash(sidebarUrl);

      if (children) {
        return (
          <SubMenu
            key={key}
            title={
              <span className="isoMenuHolder" style={submenuColor}>
                <i className={leftIcon} />
                <span className="nav-text">
                  <IntlMessages id={label} />
                </span>
              </span>
            }
          >
            {children.map((child) => {
              const linkTo = child.withoutDashboard
                ? `/${child.key}`
                : `${url}/${child.key}`;
              return (
                <Menu.Item style={submenuStyle} key={child.key}>
                  <Link style={submenuColor} to={linkTo}>
                    <IntlMessages id={child.label} />
                  </Link>
                </Menu.Item>
              );
            })}
          </SubMenu>
        );
      }
      return (
        <Menu.Item key={key} style={{ whiteSpace: "normal" }}>
          <Link to={`${url}/${key}`}>
            <span className="isoMenuHolder" style={submenuColor}>
              <FontAwesomeIcon icon={leftIcon} />
              <span className="nav-text" style={{ lineHeight: "15px" }}>
                <IntlMessages id={label} />
              </span>
            </span>
          </Link>
        </Menu.Item>
      );
    },
    [sidebarUrl],
  );

  const collapsed = clone(app.collapsed) && !clone(app.openDrawer);
  const { openDrawer } = app;
  const mode = collapsed === true ? "vertical" : "inline";
  const onMouseEnter = () => {
    if (openDrawer === false) {
      toggleOpenDrawer();
    }
  };
  const onMouseLeave = () => {
    if (openDrawer === true) {
      toggleOpenDrawer();
    }
  };
  const customizedTheme = themes[themeConfig.theme];
  const styling = {
    backgroundColor: customizedTheme.backgroundColor,
  };
  const submenuStyle = {
    backgroundColor: "rgba(0,0,0,0.3)",
    color: customizedTheme.textColor,
  };
  const submenuColor = {
    color: customizedTheme.textColor,
  };

  return (
    <SidebarWrapper>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={240}
        className="isomorphicSidebar"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={styling}
      >
        <Logo collapsed={collapsed} />
        <Scrollbars style={{ height: height - 70 }}>
          <Menu
            onClick={handleClick}
            theme="dark"
            className="isoDashboardMenu"
            mode={mode}
            openKeys={collapsed ? [] : app.openKeys}
            selectedKeys={app.current}
            onOpenChange={onOpenChange}
          >
            {options
              .filter((option) =>
                satisfyPermissionSets(
                  data?.data,
                  option.requiredPermissionSets,
                  option.requireSuperPlusAdmin,
                ),
              )
              .map((singleOption) =>
                getMenuItem({
                  submenuStyle,
                  submenuColor,
                  singleOption,
                }),
              )}
          </Menu>
        </Scrollbars>
      </Sider>
    </SidebarWrapper>
  );
};

export default connect(
  (state) => ({
    app: state.App,
    locale: state.LanguageSwitcher.language.locale,
    height: state.App.height,
    accountInfo: state.getMyAccountInfo.data,
  }),
  {
    toggleOpenDrawer,
    changeOpenKeys,
    changeCurrent,
    toggleCollapsed,
  },
)(Sidebar);
